/**
 * WebAuthn PRF (Pseudo-Random Function) Extension
 *
 * This module handles WebAuthn credential registration and authentication
 * with the PRF extension for deriving encryption keys.
 *
 * The PRF extension allows us to derive deterministic encryption keys from
 * biometric authentication (Face ID / Touch ID), enabling passwordless
 * local backup encryption.
 *
 * IMPORTANT: PRF-encrypted backups should NEVER be exported - they're tied to
 * the specific passkey. Always use password encryption for backup exports.
 *
 * References:
 * - WebAuthn PRF explainer: https://github.com/w3c/webauthn/wiki/Explainer:-PRF-extension
 * - Yubico PRF guide: https://developers.yubico.com/WebAuthn/Concepts/PRF_Extension/Developers_Guide_to_PRF.html
 * - Bitwarden PRF: https://bitwarden.com/blog/prf-webauthn-and-its-role-in-passkeys/
 */

import {
	arrayBufferToBase64,
	arrayBufferToBase64URL,
	base64ToArrayBuffer,
	base64URLToArrayBuffer,
	deriveEncryptionKeyFromPRF,
	generatePRFSalt,
} from "./encryption";
import type {
	AuthenticationExtensionsClientInputsWithPRF,
	AuthenticationExtensionsClientOutputsWithPRF,
	PRFAuthResult,
	PRFCredentialClient,
	PRFRegistrationResult,
} from "./types";

/**
 * Check if the browser supports WebAuthn PRF extension
 *
 * Note: This is a basic check. Actual PRF support depends on the authenticator.
 * We verify actual support during registration by checking the response.
 */
export async function checkPRFSupport(): Promise<boolean> {
	if (typeof window === "undefined") {
		return false;
	}
	if (!window.PublicKeyCredential) {
		return false;
	}

	// Check if platform authenticator is available
	try {
		const available =
			await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
		return available;
	} catch {
		return false;
	}
}

/**
 * Register a new WebAuthn credential with PRF support
 *
 * This creates a new passkey that can be used for:
 * 1. Biometric authentication
 * 2. PRF-based encryption key derivation
 *
 * @param options - Registration options from server
 * @returns Registration result with credential info
 */
export async function registerPRFCredential(options: {
	challenge: ArrayBuffer;
	rpId: string;
	rpName: string;
	userId: ArrayBuffer;
	userName: string;
	userDisplayName: string;
	excludeCredentials?: Array<{ id: ArrayBuffer; type: "public-key" }>;
}): Promise<PRFRegistrationResult> {
	try {
		// Generate PRF salt for this credential
		const prfSalt = generatePRFSalt();

		// Create credential with PRF extension
		const credential = (await navigator.credentials.create({
			publicKey: {
				challenge: options.challenge,
				rp: {
					id: options.rpId,
					name: options.rpName,
				},
				user: {
					id: options.userId,
					name: options.userName,
					displayName: options.userDisplayName,
				},
				pubKeyCredParams: [
					{ alg: -7, type: "public-key" }, // ES256
					{ alg: -257, type: "public-key" }, // RS256
				],
				authenticatorSelection: {
					// No authenticatorAttachment: allow a platform passkey
					// (Face ID/Touch ID) or a cross-device/syncable one (phone via
					// QR). residentKey "required" makes it discoverable.
					residentKey: "required",
					userVerification: "required",
				},
				timeout: 60_000,
				excludeCredentials: options.excludeCredentials,
				attestation: "none",
				extensions: {
					// Request PRF support check during registration
					// Note: We test PRF during first auth, not registration
					credProps: true,
				} as AuthenticationExtensionsClientInputs,
			},
		})) as PublicKeyCredential | null;

		if (!credential) {
			return {
				success: false,
				error: "Credential creation was cancelled",
			};
		}

		const response = credential.response as AuthenticatorAttestationResponse;
		const credentialId = arrayBufferToBase64URL(credential.rawId);

		// Get extension results to check for PRF support indication
		const _extensionResults = credential.getClientExtensionResults();

		// We'll verify actual PRF support during first authentication
		// For now, assume platform authenticators on modern browsers support PRF
		return {
			success: true,
			credentialId,
			prfSupported: true, // Will verify on first auth
			prfSalt: arrayBufferToBase64(prfSalt),
			// Include data needed for server storage
			_internal: {
				publicKey: arrayBufferToBase64(
					(() => {
						const pk = response.getPublicKey();
						if (!pk) {
							throw new Error(
								"Failed to get public key from attestation response"
							);
						}
						return pk;
					})()
				),
				attestationObject: arrayBufferToBase64(response.attestationObject),
				clientDataJSON: arrayBufferToBase64(response.clientDataJSON),
				transports: response.getTransports?.() || [],
				prfSalt: arrayBufferToBase64(prfSalt),
			},
		} as PRFRegistrationResult & { _internal: Record<string, unknown> };
	} catch (error) {
		console.error("[registerPRFCredential] Error:", error);
		return {
			success: false,
			error: error instanceof Error ? error.message : "Registration failed",
		};
	}
}

/**
 * Authenticate with WebAuthn PRF to derive encryption key
 *
 * This performs a WebAuthn authentication ceremony with the PRF extension
 * to derive a deterministic encryption key from the user's passkey.
 *
 * @param credential - The credential to authenticate with (includes PRF salt)
 * @param challenge - Challenge from server
 * @param rpId - Relying Party ID
 * @returns PRF authentication result with derived encryption key
 */
export async function authenticateWithPRF(options: {
	credential: PRFCredentialClient;
	challenge: ArrayBuffer;
	rpId: string;
}): Promise<PRFAuthResult> {
	try {
		// Prepare PRF extension inputs
		const prfExtension: AuthenticationExtensionsClientInputsWithPRF = {
			prf: {
				eval: {
					first: options.credential.prfSalt,
				},
			},
		};

		// Perform WebAuthn authentication with PRF
		const assertion = (await navigator.credentials.get({
			publicKey: {
				challenge: options.challenge,
				rpId: options.rpId,
				allowCredentials: [
					{
						id: options.credential.credentialId,
						type: "public-key",
						// internal = this device's authenticator; hybrid = the
						// cross-device (QR-to-phone / caBLE) flow.
						transports: ["internal", "hybrid"],
					},
				],
				userVerification: "required",
				timeout: 60_000,
				extensions: prfExtension as AuthenticationExtensionsClientInputs,
			},
		})) as PublicKeyCredential | null;

		if (!assertion) {
			return {
				success: false,
				error: "Authentication was cancelled",
			};
		}

		// Get PRF results from extension outputs
		const extensionResults =
			assertion.getClientExtensionResults() as AuthenticationExtensionsClientOutputsWithPRF;

		if (!extensionResults.prf?.results?.first) {
			// PRF not supported or failed
			return {
				success: false,
				error:
					"PRF extension not supported on this device. Please use password authentication.",
			};
		}

		// Derive encryption key from PRF output
		const prfOutput = extensionResults.prf.results.first;
		const encryptionKey = await deriveEncryptionKeyFromPRF(prfOutput);

		return {
			success: true,
			encryptionKey,
			credentialId: arrayBufferToBase64URL(assertion.rawId),
		};
	} catch (error) {
		console.error("[authenticateWithPRF] Error:", error);
		return {
			success: false,
			error: error instanceof Error ? error.message : "Authentication failed",
		};
	}
}

/**
 * Convert server-stored credential to client format
 */
export function toClientCredential(serverCredential: {
	credentialId: string;
	prfSalt: string;
	deviceName?: string;
}): PRFCredentialClient {
	return {
		credentialId: base64URLToArrayBuffer(serverCredential.credentialId),
		prfSalt: base64ToArrayBuffer(serverCredential.prfSalt),
		deviceName: serverCredential.deviceName,
	};
}

/**
 * Generate a random challenge for WebAuthn ceremonies
 * In production, this should come from the server
 */
export function generateChallenge(): ArrayBuffer {
	return crypto.getRandomValues(new Uint8Array(32)).buffer;
}
